import type { CharacterRig } from './types';
import {
  getCharacterAnimationCapability,
  type CharacterAnimationPriority,
} from './animation-capabilities';

export interface CharacterAnimationOverride {
  readonly alias: string;
  readonly untilMs: number;
}

export const CHARACTER_REACTION_ANIMATION_MS = 1_200;
export const CHARACTER_REACTION_ANIMATION_BUNDLES = ['actions', 'emotes'] as const;

export function setCharacterAnimationOverride(
  overrides: Map<string, CharacterAnimationOverride>,
  id: string,
  alias: string,
  nowMs: number,
  durationMs = CHARACTER_REACTION_ANIMATION_MS,
): void {
  overrides.set(id, { alias, untilMs: nowMs + durationMs });
}

export function selectCharacterAnimationAlias(args: {
  readonly overrides: Map<string, CharacterAnimationOverride>;
  readonly id: string;
  readonly fallbackAlias: string;
  readonly nowMs: number;
}): string {
  const override = args.overrides.get(args.id);
  if (override == null) return args.fallbackAlias;
  if (override.untilMs <= args.nowMs) {
    args.overrides.delete(args.id);
    return args.fallbackAlias;
  }
  if (
    priorityValue(animationPriority(args.fallbackAlias)) >
    priorityValue(animationPriority(override.alias))
  ) {
    return args.fallbackAlias;
  }
  return override.alias;
}

export function playCharacterAnimationAlias(
  rig: Pick<CharacterRig, 'playAnimation'>,
  alias: string,
  fallbackAlias: string,
): string {
  if (rig.playAnimation?.(alias) === true) return alias;
  if (alias !== fallbackAlias) rig.playAnimation?.(fallbackAlias);
  return fallbackAlias;
}

function animationPriority(alias: string): CharacterAnimationPriority {
  if (alias === 'idle') return 'idle';
  return getCharacterAnimationCapability(alias)?.priority ?? 'movement';
}

function priorityValue(priority: CharacterAnimationPriority): number {
  switch (priority) {
    case 'critical':
      return 3;
    case 'movement':
      return 2;
    case 'emote':
      return 1;
    case 'idle':
      return 0;
  }
}
